(() => {
  const DIMENSIONS = new Map([
    ['width', { label: 'Width', unit: 'cm' }],
    ['depth', { label: 'Depth', unit: 'cm' }],
    ['height', { label: 'Height', unit: 'cm' }],
    ['seat height', { label: 'Seat Height', unit: 'cm' }],
    ['seat height (cm)', { label: 'Seat Height', unit: 'cm' }],
  ]);
  const CHIP_FILTERS = new Set(['colour', 'color', 'main material']);
  const FILTER_ROOT_SELECTOR = 'div.facets-block-wrapper--vertical, .facets--horizontal, #filters-drawer';
  const observedRoots = new WeakSet();
  let enhanceQueued = false;

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const parseNumber = (value) => {
    const match = String(value || '').trim().replace(',', '.').match(/-?\d+(?:\.\d+)?/);
    return match ? Number.parseFloat(match[0]) : Number.NaN;
  };
  const formatNumber = (value) => Number.isFinite(value) ? (Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '')) : '';
  const formatRangeValue = (value, unit = '') => `${formatNumber(value)}${unit ? ` ${unit}` : ''}`;
  const uniqueSorted = (values) => [...new Set(values.filter(Number.isFinite))].sort((a, b) => a - b);

  function getInputNumber(input) {
    if (!(input instanceof HTMLInputElement)) return Number.NaN;
    for (const source of [input.dataset.label, input.getAttribute('aria-label')]) {
      const parsed = parseNumber(source);
      if (Number.isFinite(parsed)) return parsed;
    }
    const ownLabel = input.closest('label');
    if (ownLabel) {
      const parsed = parseNumber(ownLabel.textContent);
      if (Number.isFinite(parsed)) return parsed;
    }
    if (input.id) {
      try {
        const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        const parsed = parseNumber(label?.textContent);
        if (Number.isFinite(parsed)) return parsed;
      } catch (_error) {}
    }
    return parseNumber(input.value);
  }

  function setRangePaint(control, low, high, min, max) {
    const span = max - min || 1;
    control.style.setProperty('--range-start', `${Math.max(0, Math.min(100, ((low - min) / span) * 100))}%`);
    control.style.setProperty('--range-end', `${Math.max(0, Math.min(100, ((high - min) / span) * 100))}%`);
  }

  function createDualRange({ min, max, low, high, step = 0.1, unit = '', ariaLabel, onCommit }) {
    const root = document.createElement('div');
    root.className = 'deliving-range-filter';
    const values = document.createElement('div');
    values.className = 'deliving-range-values';
    const lowText = document.createElement('span');
    const highText = document.createElement('span');
    values.append(lowText, highText);
    const control = document.createElement('div');
    control.className = 'deliving-range-control';
    control.innerHTML = '<div class="deliving-range-track"></div><div class="deliving-range-fill"></div>';
    const lowInput = document.createElement('input');
    const highInput = document.createElement('input');
    [lowInput, highInput].forEach((input, index) => {
      input.type = 'range';
      input.min = String(min);
      input.max = String(max);
      input.step = String(step);
      input.className = `deliving-range-input deliving-range-input--${index === 0 ? 'low' : 'high'}`;
      input.setAttribute('aria-label', `${ariaLabel} ${index === 0 ? 'minimum' : 'maximum'}`);
    });
    lowInput.value = String(low);
    highInput.value = String(high);
    control.append(lowInput, highInput);
    root.append(values, control);

    const refresh = (source) => {
      let lowValue = Number.parseFloat(lowInput.value);
      let highValue = Number.parseFloat(highInput.value);
      if (source === lowInput && lowValue > highValue) lowInput.value = String(lowValue = highValue);
      if (source === highInput && highValue < lowValue) highInput.value = String(highValue = lowValue);
      lowText.textContent = formatRangeValue(lowValue, unit);
      highText.textContent = formatRangeValue(highValue, unit);
      setRangePaint(control, lowValue, highValue, min, max);
      return { low: lowValue, high: highValue };
    };
    lowInput.addEventListener('input', () => refresh(lowInput));
    highInput.addEventListener('input', () => refresh(highInput));
    lowInput.addEventListener('change', () => { const v = refresh(lowInput); onCommit(v.low, v.high); });
    highInput.addEventListener('change', () => { const v = refresh(highInput); onCommit(v.low, v.high); });
    refresh();
    return root;
  }

  function enhanceDimensionList(list) {
    if (!(list instanceof HTMLElement) || list.dataset.delivingRangeEnhanced === 'true') return;
    const config = DIMENSIONS.get(normalize(list.getAttribute('name')));
    if (!config) return;
    const numericInputs = [...list.querySelectorAll('input[type="checkbox"]')]
      .filter((input) => input instanceof HTMLInputElement)
      .map((input) => ({ input, value: getInputNumber(input) }))
      .filter(({ value }) => Number.isFinite(value));
    const values = uniqueSorted(numericInputs.map(({ value }) => value));
    if (values.length < 2) return;
    const facetItem = list.closest('.facets__item');
    const inputComponent = list.closest('facet-inputs-component');
    const valuesWrapper = list.closest('.facets__inputs-wrapper');
    if (!facetItem || !inputComponent || !valuesWrapper) return;
    const fullMin = values[0];
    const fullMax = values[values.length - 1];
    const activeValues = uniqueSorted(numericInputs.filter(({ input }) => input.checked).map(({ value }) => value));
    const initialLow = activeValues.length ? activeValues[0] : fullMin;
    const initialHigh = activeValues.length ? activeValues[activeValues.length - 1] : fullMax;
    list.dataset.delivingRangeEnhanced = 'true';
    if (config.label === 'Seat Height') list.dataset.delivingSeatRangeEnhanced = 'true';
    facetItem.classList.add('deliving-dimension-filter');
    if (config.label === 'Seat Height') facetItem.classList.add('deliving-seat-height-filter');
    facetItem.dataset.delivingFilterLabel = config.label;
    valuesWrapper.classList.add('deliving-original-filter-values');
    inputComponent.querySelector('.show-more__button')?.classList.add('deliving-original-filter-values');
    const range = createDualRange({
      min: fullMin, max: fullMax, low: initialLow, high: initialHigh, step: 0.1, unit: config.unit, ariaLabel: config.label,
      onCommit: (low, high) => {
        const isFullRange = low <= fullMin && high >= fullMax;
        let eventTarget = null;
        numericInputs.forEach(({ input, value }) => {
          if (input.disabled) return;
          const shouldCheck = !isFullRange && value >= low && value <= high;
          if (input.checked !== shouldCheck) { input.checked = shouldCheck; eventTarget = input; }
        });
        eventTarget = eventTarget || numericInputs.find(({ input }) => !input.disabled)?.input || null;
        eventTarget?.dispatchEvent(new Event('change', { bubbles: true }));
      },
    });
    if (config.label === 'Seat Height') range.classList.add('deliving-seat-height-range');
    inputComponent.prepend(range);
  }

  function parsePrice(value) {
    const text = String(value || '').trim();
    if (!text) return Number.NaN;
    const parsed = Number.parseFloat(text.replace(/[^\d.,-]/g, '').replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }

  function enhancePriceFacet(component) {
    if (!(component instanceof HTMLElement) || component.dataset.delivingRangeEnhanced === 'true') return;
    const minInput = component.querySelector('input[name="filter.v.price.gte"]');
    const maxInput = component.querySelector('input[name="filter.v.price.lte"]');
    if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) return;
    const fullMin = 0;
    const fullMax = parsePrice(maxInput.dataset.max || maxInput.placeholder);
    if (!Number.isFinite(fullMax) || fullMax <= 0) return;
    const currentMin = parsePrice(minInput.value);
    const currentMax = parsePrice(maxInput.value);
    component.dataset.delivingRangeEnhanced = 'true';
    component.classList.add('deliving-price-enhanced');
    const range = createDualRange({
      min: fullMin, max: fullMax,
      low: Number.isFinite(currentMin) ? currentMin : fullMin,
      high: Number.isFinite(currentMax) ? currentMax : fullMax,
      step: 1, ariaLabel: 'Price',
      onCommit: (low, high) => {
        minInput.value = low <= fullMin ? '' : formatNumber(low);
        maxInput.value = high >= fullMax ? '' : formatNumber(high);
        maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      },
    });
    range.classList.add('deliving-price-range');
    range.querySelector('.deliving-range-values')?.classList.add('deliving-range-values--price');
    const labels = range.querySelectorAll('.deliving-range-values span');
    const updateLabels = () => {
      const low = range.querySelector('.deliving-range-input--low');
      const high = range.querySelector('.deliving-range-input--high');
      if (!(low instanceof HTMLInputElement) || !(high instanceof HTMLInputElement)) return;
      labels[0].textContent = `£${formatNumber(Number.parseFloat(low.value))}`;
      labels[1].textContent = `£${formatNumber(Number.parseFloat(high.value))}`;
    };
    range.querySelectorAll('.deliving-range-input').forEach((input) => input.addEventListener('input', updateLabels));
    updateLabels();
    component.prepend(range);
  }

  function enhanceChipFilters(list) {
    if (!(list instanceof HTMLElement) || !CHIP_FILTERS.has(normalize(list.getAttribute('name')))) return;
    list.closest('.facets__item')?.classList.add('deliving-chip-filter');
  }

  function buildDimensionParamMap(form) {
    const map = new Map();
    form.querySelectorAll('ul[name]').forEach((list) => {
      const config = DIMENSIONS.get(normalize(list.getAttribute('name')));
      if (!config) return;
      const input = list.querySelector('input[type="checkbox"][name]');
      if (input instanceof HTMLInputElement) map.set(input.name, { label: config.label, unit: config.unit, list });
    });
    return map;
  }

  function inferRemovedParam(currentParams, removeUrl, candidates) {
    let removal;
    try { removal = new URL(removeUrl, window.location.origin).searchParams; } catch (_error) { return null; }
    for (const paramName of candidates.keys()) {
      if (removal.getAll(paramName).length < currentParams.getAll(paramName).length) return paramName;
    }
    return null;
  }

  function enhanceActiveFilters(form) {
    const remove = form.querySelector('.facets-remove');
    if (!(remove instanceof HTMLElement) || remove.dataset.delivingEnhanced === 'true') return;
    const dimensionParams = buildDimensionParamMap(form);
    const currentParams = new URL(window.location.href).searchParams;
    const dimensionPills = new Map();
    remove.querySelectorAll('facet-remove-component.facets-remove__pill[data-url]').forEach((pill) => {
      const paramName = inferRemovedParam(currentParams, pill.dataset.url || '', dimensionParams);
      if (!paramName) return;
      pill.classList.add('deliving-hidden-active-pill');
      const bucket = dimensionPills.get(paramName) || [];
      bucket.push(pill);
      dimensionPills.set(paramName, bucket);
    });
    const clearComponent = [...remove.querySelectorAll(':scope > facet-remove-component')].find((component) => !component.classList.contains('facets-remove__pill'));
    const formComponent = form.closest('facets-form-component');
    dimensionPills.forEach((_pills, paramName) => {
      const config = dimensionParams.get(paramName);
      if (!config) return;
      const checked = [...config.list.querySelectorAll('input[type="checkbox"]:checked')].map(getInputNumber).filter(Number.isFinite).sort((a,b)=>a-b);
      if (!checked.length) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pills__pill pills__pill--desktop-small facets-remove__pill deliving-range-active-pill';
      if (config.label === 'Seat Height') button.classList.add('deliving-seat-range-active-pill');
      button.innerHTML = `<span>${config.label}: ${formatRangeValue(checked[0], config.unit)} – ${formatRangeValue(checked[checked.length - 1], config.unit)}</span><span aria-hidden="true" class="deliving-pill-close">×</span>`;
      button.addEventListener('click', () => {
        const url = new URL(window.location.href);
        url.searchParams.delete(paramName);
        if (formComponent && typeof formComponent.updateFiltersByURL === 'function') formComponent.updateFiltersByURL(url.toString());
        else window.location.assign(url.toString());
      });
      if (clearComponent) remove.insertBefore(button, clearComponent); else remove.append(button);
    });
    const visiblePills = remove.querySelectorAll('.facets-remove__pill:not(.deliving-hidden-active-pill)');
    if (visiblePills.length) {
      const header = document.createElement('div');
      header.className = 'deliving-active-filters-header';
      header.innerHTML = '<strong>Active filters</strong>';
      if (clearComponent instanceof HTMLElement && clearComponent.dataset.url) {
        const clearButton = document.createElement('button');
        clearButton.type = 'button';
        clearButton.className = 'deliving-clear-all';
        clearButton.textContent = 'Clear all';
        clearButton.addEventListener('click', () => {
          const url = new URL(clearComponent.dataset.url, window.location.origin);
          if (formComponent && typeof formComponent.updateFiltersByURL === 'function') formComponent.updateFiltersByURL(url.toString());
          else window.location.assign(url.toString());
        });
        header.append(clearButton);
        clearComponent.classList.add('deliving-native-clear-hidden');
      }
      remove.prepend(header);
    }
    remove.dataset.delivingEnhanced = 'true';
    if (dimensionParams.size) form.dataset.delivingSeatPillEnhanced = 'true';
  }

  function enhanceSidebar(form) {
    if (!(form instanceof HTMLFormElement) || form.dataset.delivingSidebarEnhanced === 'true' || !form.closest('div.facets-block-wrapper--vertical')) return;
    const title = document.createElement('div');
    title.className = 'deliving-filter-title';
    title.innerHTML = '<h3>Filters</h3>';
    form.prepend(title);
    const actions = document.createElement('div');
    actions.className = 'deliving-filter-actions';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'deliving-view-results';
    button.textContent = 'View results';
    button.addEventListener('click', () => document.querySelector('#ResultsList, [data-testid="product-grid"], .product-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    actions.append(button);
    const countSource = document.querySelector('.facets--horizontal .products-count-wrapper span[role="status"]');
    if (countSource?.textContent?.trim()) {
      const count = document.createElement('div');
      count.className = 'deliving-results-count';
      count.textContent = countSource.textContent.trim();
      actions.append(count);
    }
    form.append(actions);
    form.dataset.delivingSidebarEnhanced = 'true';
  }

  function enhance(root = document) {
    root.querySelectorAll?.('ul[name]').forEach((list) => { enhanceDimensionList(list); enhanceChipFilters(list); });
    root.querySelectorAll?.('price-facet-component').forEach(enhancePriceFacet);
    root.querySelectorAll?.('div.facets-block-wrapper--vertical .facets__form').forEach((form) => { enhanceActiveFilters(form); enhanceSidebar(form); });
  }

  function queueEnhance() {
    if (enhanceQueued) return;
    enhanceQueued = true;
    requestAnimationFrame(() => { enhanceQueued = false; enhance(); discoverFilterRoots(); });
  }

  function attachFilterObserver(root) {
    if (!(root instanceof Element) || observedRoots.has(root)) return;
    observedRoots.add(root);
    new MutationObserver(queueEnhance).observe(root, { childList: true, subtree: true });
  }

  function discoverFilterRoots() {
    document.querySelectorAll(FILTER_ROOT_SELECTOR).forEach(attachFilterObserver);
  }

  function init() {
    discoverFilterRoots();
    queueEnhance();
    const resultsList = document.querySelector('results-list');
    if (resultsList) {
      new MutationObserver(() => { discoverFilterRoots(); queueEnhance(); }).observe(resultsList, { childList: true, subtree: false });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.addEventListener('popstate', queueEnhance);
})();
